import { action, computed, observable } from "mobx";
import { inject, injectable, interfaces } from "inversify";
import { ScreenBase } from "@frui.ts/screens";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import LocalizationService from "../../services/localizationService";
import React from "react";
import TurnstileService from "services/turnstileServise";

export default class TurnstileViewModel extends ScreenBase {
  turnstileRef = React.createRef<TurnstileInstance>();
  @observable showInteractive = false;
  @observable isDisabledByTurnsitel = false;

  constructor(
    public localizationService: LocalizationService,
    private turnstileService: TurnstileService,
    public context?: string,
    public shareToken = false
  ) {
    super();
    this.shareToken = shareToken;
    this.initialize(context);
  }

  @action.bound
  async initialize(context?: string): Promise<void> {
    this.turnstileService.initialize(context ?? "default");
  }

  @computed
  get turnstileToken(): string | undefined {
    return this.turnstileService.token;
  }

  @computed
  get turnstileInfo(): string | undefined {
    return this.turnstileService.info;
  }

  @computed
  get turnstileError(): string | undefined {
    return this.turnstileService.error;
  }

  @computed
  get isValid(): boolean {
    return this.turnstileService.isValid;
  }

  @action.bound
  onTurnstileVerify = (token: string): void => {
    this.turnstileService.setToken(token);
  };

  @action.bound
  onTurnstileError = (error: string): void => {
    this.turnstileService.setError(error);
  };

  @action.bound
  onBeforeInteractive = (string: string): void => {
    this.turnstileService.setInfo(string);
  };

  @action.bound
  onSetInfo = (string: string): void => {
    this.turnstileService.setInfo(string);
  };

  @action.bound
  onTurnstileLoad = (): void => {
    this.turnstileService.setInfo(this.translateTurnstile("loading"));
  };

  @action.bound
  reset(): void {
    this.showInteractive = false;
    this.turnstileService.reset();
  }

  @action.bound
  resetError(): void {
    this.turnstileService.setError(undefined);
  }

  @action.bound
  resetToken(): void {
    this.turnstileService.resetToken();
  }

  translateTurnstile(key: string): string {
    return this.turnstileService.translateTurnstile(key);
  }

  @action.bound
  setInstance(instance: TurnstileInstance): void {
    this.turnstileService.setInstance(instance);
    this.turnstileRef = { current: instance } as React.RefObject<TurnstileInstance>;
  }

  static Factory({ container }: interfaces.Context) {
    return (context?: string, shareToken = false) => {
      return new TurnstileViewModel(container.get(LocalizationService), container.get(TurnstileService), context, shareToken);
    };
  }
}
